import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from 'react';
import { isAuthenticated, initializeAuth, logout as authLogout } from '../utils/auth';
import { initializeSessionTimeout, stopSessionTimeout, getSessionTimeoutManager } from '../utils/sessionTimeout';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';
const AuthContext = createContext(undefined);
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [warningMinutes, setWarningMinutes] = useState(0);
    // Session timeout callbacks
    const sessionTimeoutCallbacks = {
        onWarning: (remainingMinutes) => {
            setWarningMinutes(remainingMinutes);
            setShowTimeoutWarning(true);
        },
        onTimeout: async () => {
            console.log('Session timeout - automatically logging out user');
            setShowTimeoutWarning(false);
            await logout();
        }
    };
    useEffect(() => {
        const initAuth = async () => {
            try {
                setIsLoading(true);
                const currentUser = await initializeAuth();
                setUser(currentUser);
                // Start session timeout monitoring if user is authenticated
                if (currentUser) {
                    initializeSessionTimeout(sessionTimeoutCallbacks, {
                        timeoutMinutes: 30, // 30 minute timeout
                        warningMinutes: 5, // 5 minute warning
                        checkIntervalSeconds: 60 // Check every minute
                    }).start();
                }
            }
            catch (error) {
                console.error('Auth initialization error:', error);
                setUser(null);
            }
            finally {
                setIsLoading(false);
            }
        };
        initAuth();
        // Cleanup session timeout on unmount
        return () => {
            stopSessionTimeout();
        };
    }, []);
    const login = (userData) => {
        setUser(userData);
        // Start session timeout monitoring on login
        initializeSessionTimeout(sessionTimeoutCallbacks, {
            timeoutMinutes: 30,
            warningMinutes: 5,
            checkIntervalSeconds: 60
        }).start();
    };
    const logout = async () => {
        try {
            await authLogout();
        }
        catch (error) {
            console.error('Logout error:', error);
        }
        finally {
            // Stop session timeout monitoring
            stopSessionTimeout();
            setUser(null);
            setShowTimeoutWarning(false);
        }
    };
    const updateUser = (userData) => {
        setUser(userData);
    };
    const refreshUser = async () => {
        if (!isAuthenticated)
            return;
        try {
            const currentUser = await initializeAuth();
            if (currentUser) {
                setUser(currentUser);
            }
        }
        catch (error) {
            console.error('Failed to refresh user data:', error);
        }
    };
    // Session timeout warning handlers
    const handleExtendSession = () => {
        const manager = getSessionTimeoutManager();
        if (manager) {
            manager.extendSession();
            setShowTimeoutWarning(false);
            console.log('Session extended by user');
        }
    };
    const handleLogoutNow = async () => {
        await logout();
    };
    const value = {
        user,
        isLoading,
        isAuthenticated: user !== null,
        login,
        logout,
        updateUser,
        refreshUser,
    };
    return (_jsxs(AuthContext.Provider, { value: value, children: [children, _jsx(SessionTimeoutWarning, { isVisible: showTimeoutWarning, remainingMinutes: warningMinutes, onExtendSession: handleExtendSession, onLogoutNow: handleLogoutNow })] }));
};
